import React from 'react'

import { Source } from 'types/GlobalSource'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericListItem from 'components_new/molecules/GenericListItem'

import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

import { DATABASE_CARDS, SOURCES_TO_SKIP, TSelectedSource } from '../lib'

import FileIcon from 'assets/collect/file.svg'

import { ESourceCategory, SOURCE_CATEGORY_META } from 'utils/sources'

interface SelectSourceProps {
  category: ESourceCategory
  onSelect: (selected: TSelectedSource) => void
  search: string
  sources: { [id: string]: Source }
}

/**
 * Turns the store's sources into what the picker shows. Databases are the odd
 * one out: every engine goes through the single OnPremiseDirect source, so the
 * rows are hardcoded and only differ by which driver they ask for.
 */
const getRows = (
  category: ESourceCategory,
  sources: { [id: string]: Source }
): TSelectedSource[] => {
  const all = Object.values(sources)

  if (category === ESourceCategory.DATABASE) {
    const source = all.find(
      (item) => item.attributes.type === 'OnPremiseDirect'
    )

    if (!source) {
      return []
    }

    return DATABASE_CARDS.map((card) => ({
      databaseType: card.database,
      imgSrc: card.imgSrc,
      source,
      title: card.title
    }))
  }

  if (category === ESourceCategory.FILE) {
    const source = all.find((item) => item.attributes.type === 'File')

    return source
      ? [{ imgSrc: FileIcon, source, title: source.attributes.name }]
      : []
  }

  // KPI_API sources are systems too, they just answer per metric.
  const types = ['API', 'KPI_API']

  return all
    .filter(
      (source) =>
        types.includes(source.attributes.type) &&
        !(source.attributes.name in SOURCES_TO_SKIP)
    )
    .map((source) => ({
      imgSrc: source.attributes.logo_url ?? undefined,
      source,
      title: source.attributes.name
    }))
}

const SelectSource = (props: SelectSourceProps) => {
  const { category, onSelect, search, sources } = props

  const rows = getRows(category, sources)
    .filter((row) => row.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.title.localeCompare(b.title))

  if (rows.length === 0) {
    return <EmptyPlaceholder title="Hittade inga datakällor" />
  }

  return (
    <>
      {rows.map((row) => (
        <GenericListItem
          action="add"
          body={row.source.attributes.description ?? undefined}
          borderBottom={true}
          customTitle={<Highlighted highlight={search} text={row.title} />}
          icon={
            row.imgSrc
              ? undefined
              : { name: SOURCE_CATEGORY_META[category].icon }
          }
          key={`${row.title}-${row.source.id}`}
          onClick={() => onSelect(row)}
          src={row.imgSrc}
          sx={{ maxHeight: 64 }}
          title={row.title}
        />
      ))}
    </>
  )
}

export default SelectSource
